import type { ExhibitionFields } from '@repo/api-contracts';
import { trpc } from '@/shared/api';
import { resetSettledRequests } from '@/shared/lib';
import { useExhibitionImageUpload } from './use-exhibition-image-upload';

interface UseCreateExhibitionOptions {
  readonly onSuccess?: () => void;
}

/** 대표 이미지를 골랐으면 먼저 올리고, 기획전을 숨김 상태로 목적 맨 뒤에 만든다. */
export const useCreateExhibition = ({ onSuccess }: UseCreateExhibitionOptions = {}) => {
  const uploader = useExhibitionImageUpload();
  const mutation = trpc.productManagement.exhibitions.create.useMutation({ onSuccess });

  /**
   * 성공하면 true — 폼 모달을 닫는 데 쓴다. 실패 원인은 errorMessage 로 모달에 남는다.
   *
   * 업로드와 만들기는 오류 한 줄(errorMessage)을 함께 쓴다. 요청을 보내기 직전에 다른 쪽의 지난 오류를
   * 지운다 — 대표 이미지는 고르지 않아도 되어, 업로드가 거부된 뒤 파일 없이 다시 제출하면 업로드를 건너뛰고
   * 옛 업로드 오류가 만들기의 거부 문구를 가린다.
   */
  const createExhibition = async (
    fields: ExhibitionFields,
    file: File | null,
  ): Promise<boolean> => {
    let imageKey: string | null = null;

    if (file !== null) {
      // 업로드가 사전 검사(형식·크기)에 막혀도 그 문구가 오류 한 줄의 앞자리에 올라 지난 거부는 보이지 않는다.
      resetSettledRequests([mutation]);
      imageKey = await uploader.upload(file);

      if (imageKey === null) {
        return false;
      }
    }

    // 이 제출의 업로드는 위에서 끝났거나 건너뛰었다 — 남은 업로드 오류는 지난 제출의 것이다.
    uploader.clearError();

    try {
      await mutation.mutateAsync({ ...fields, ...(imageKey === null ? {} : { imageKey }) });
      return true;
    } catch {
      // 서버 거부 문구는 mutation.error 로 남아 모달에 보인다 — 여기서는 닫지 않을 신호만 준다.
      return false;
    }
  };

  return {
    createExhibition,
    isPending: uploader.isUploading || mutation.isPending,
    /** 모달에 그대로 보여 줄 문구 — 업로드 사전 검사 실패가 먼저, 없으면 서버 거부 메시지. */
    errorMessage: uploader.error ?? mutation.error?.message ?? null,
    /** 지난 업로드·서버 오류를 지운다 — 모달을 새로 열고 닫을 때 쓴다. */
    reset: () => {
      mutation.reset();
      uploader.clearError();
    },
  };
};
